// src/services/auth.ts
import { 
  getAuth, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  sendPasswordResetEmail,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
  getIdToken
} from 'firebase/auth';
import app from './firebase';

export const auth = getAuth(app);

// persistenceを常にlocalに設定
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('認証永続化の設定に失敗:', error);
});

// ログイン
export const login = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const token = await getIdToken(userCredential.user);
    return { success: true, user: userCredential.user, token };
  } catch (error: any) {
    console.error('Login error:', error);
    let message = 'ログインに失敗しました';
    
    if (error.code === 'auth/user-not-found') {
      message = 'ユーザーが見つかりません';
    } else if (error.code === 'auth/wrong-password') {
      message = 'パスワードが正しくありません';
    } else if (error.code === 'auth/invalid-email') {
      message = 'メールアドレスの形式が正しくありません';
    } else if (error.code === 'auth/user-disabled') {
      message = 'このアカウントは無効化されています';
    }
    
    return { success: false, error: message };
  }
};

// Googleログイン
export const loginWithGoogle = async () => {
  try {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    
    const userCredential = await signInWithPopup(auth, provider);
    const token = await getIdToken(userCredential.user);
    return { success: true, user: userCredential.user, token };
  } catch (error: any) {
    console.error('Google login error:', error);
    let message = 'Googleログインに失敗しました';
    
    if (error.code === 'auth/popup-closed-by-user') {
      message = 'ログインがキャンセルされました';
    } else if (error.code === 'auth/popup-blocked') {
      message = 'ポップアップがブロックされました。ブラウザの設定を確認してください';
    }
    
    return { success: false, error: message };
  }
};

// 新規登録
export const register = async (email: string, password: string, displayName: string) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(userCredential.user, { displayName });
    const token = await getIdToken(userCredential.user);
    return { success: true, user: userCredential.user, token };
  } catch (error: any) {
    console.error('Register error:', error);
    let message = '登録に失敗しました';
    
    if (error.code === 'auth/email-already-in-use') {
      message = 'このメールアドレスは既に使用されています';
    } else if (error.code === 'auth/invalid-email') {
      message = 'メールアドレスの形式が正しくありません';
    } else if (error.code === 'auth/weak-password') {
      message = 'パスワードが弱すぎます。6文字以上にしてください';
    }
    
    return { success: false, error: message };
  }
};

// ログアウト
export const logout = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    console.error('Logout error:', error);
    return { success: false, error: 'ログアウトに失敗しました' };
  }
};

// パスワードリセット
export const resetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error: any) {
    console.error('Password reset error:', error);
    let message = 'パスワードリセットに失敗しました';
    
    if (error.code === 'auth/user-not-found') {
      message = 'ユーザーが見つかりません';
    } else if (error.code === 'auth/invalid-email') {
      message = 'メールアドレスの形式が正しくありません';
    }
    
    return { success: false, error: message };
  }
};

// 認証状態の監視
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// 現在のユーザーIDを取得
export const getCurrentUserId = (): string => {
  const user = auth.currentUser;
  if (!user) throw new Error('ログインが必要です');
  return user.uid;
};

// 現在のユーザー情報を取得
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

// トークンからユーザー情報を取得（自動ログイン用）
export const getUserFromToken = async (token: string) => {
  try {
    const response = await fetch('/api/verify-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });
    if (response.ok) {
      const userData = await response.json();
      return userData; // { uid, email, displayName } など
    }
    return null;
  } catch (error) {
    console.error('Token verification error:', error);
    return null;
  }
};